import { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'

// Actions
import { actions as commentsActions } from 'modules/comments/actions'

// Components
import { Heading } from '@tracklib/kit'
import AddComment from 'components/comments/addcomment'
import Comment from 'components/comments/commentitem'

// Hooks
import { useRequest } from 'modules/requests/hooks'

// Types
import { Comment as CommentType, State, EntityState } from 'types'

import styles from './index.css'

type CommentsProps = {
  objectType: string
  objectId: number
  className: string
}

const Comments = ({ objectType, objectId, className }: CommentsProps): JSX.Element => {
  const dispatch = useDispatch()
  const [commentText, setCommentText] = useState('')
  const postCommentRequest = useRequest('POST_COMMENT')
  const editCommentRequest = useRequest('EDIT_COMMENT', objectId)
  const allComments = useSelector<State, EntityState<CommentType>>(state => state.entities.comments)
  const commentsList = Object.keys(allComments).map(id => allComments[id])
  const comments = commentsList
    .filter(c => !c.parent && c.content_type === objectType && c.object_id === objectId)
    .sort((a: CommentType, b: CommentType) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())

  useEffect(() => {
    dispatch(commentsActions.fetchComments(objectType, objectId))
  }, []) // eslint-disable-line

  const handleCommentDone = () => {
    const postCommentPromise = dispatch(commentsActions.postComment(objectType, objectId, commentText))

    if (postCommentPromise) {
      postCommentPromise.then(() => {
        setCommentText('')
      })
    }
  }

  const handleUpdateCommentText = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCommentText(event.target.value)
  }

  return (
    <div className={className}>
      <Heading className={styles.title}>Comments</Heading>
      <AddComment
        isLoading={postCommentRequest.isLoading || editCommentRequest.isLoading}
        commentText={commentText}
        handleUpdateText={handleUpdateCommentText}
        onCommentDone={handleCommentDone}
        isCommenting
      />
      <ul className={styles.list}>
        {comments.map(comment => (
          <Comment comment={comment} key={comment.id} />
        ))}
      </ul>
    </div>
  )
}

export default Comments
